"use client";

import { useEffect, useState, useMemo, useRef, memo, useCallback } from "react";
import { createPortal } from "react-dom";
import { createLogger } from "@/lib/logger";
import { useRouter } from "next/navigation";
import Link from "next/link";
import LeaderAvatar from "@/app/components/LeaderAvatar";
import { isActiveDust, isClosedDust } from "../dust";

const logger = createLogger("PositionsSection");
import { formatCurrency, formatPnL } from "@/lib/format";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiShare2,
  FiTrendingUp,
  FiTrendingDown,
  FiAlertCircle,
  FiCheck,
  FiX,
  FiExternalLink,
  FiPackage,
  FiMoreVertical,
  FiRefreshCw,
  FiSearch,
  FiChevronDown,
  FiChevronUp,
  FiCopy,
} from "react-icons/fi";
import SharePositionModal from "@/app/components/SharePositionModal";

import MergePositionsModal from "@/app/components/MergePositionsModal";
// Sell modals use tradingModal store — rendered by AppLayoutWithSidebar
import { useAuth } from "@/hooks/useAuth";
import { useTradingModalStore } from "@/stores/useTradingModalStore";
import { authFetch } from "@/lib/authFetch";
import { useRedeemableQuery } from "@/hooks/useRedeemableQuery";
import { marketDisplayTitle } from "@/lib/markets/title";
import type { UnifiedPosition, PositionSource } from "../hooks/useUnifiedPositions";
import { sourceUrl, marketPageUrl } from "@/lib/source-url";
import { notifyPortfolioRefresh } from "@/lib/portfolio-refresh";
import { useT } from "@/hooks/useT";
import { formatCents } from '@/lib/format-cents';

interface Position {
  conditionId: string;
  tokenId: string;
  marketSlug: string;
  eventSlug?: string | null;
  /** Canonical event headline (gamma events[0].title) — preferred title. */
  eventTitle?: string | null;
  isMultiOutcome?: boolean;
  marketQuestion: string;
  marketSubtitle?: string;
  marketImage?: string;
  outcome: string;
  outcomeIndex: number;
  /** Kalshi-native side label ("Trump", "Above 70°F") — prefer over yes/no when present. */
  outcomeNativeLabel?: string;
  /** Polymarket per-sub-market variant ("28°C", "82-83°F") for
   *  multi-outcome events. Distinguishes which sub-market the user is
   *  positioned in when the binary `outcome` (Yes/No) is identical
   *  across every sub-market of the group. */
  groupItemTitle?: string | null;
  /** Full ordered outcomes pair from the market — source of truth for the
   *  sell modal. Backend populates this for Polymarket; frontend never
   *  re-derives it from `outcome`. */
  outcomes?: string[] | null;
  shares: number;
  avgPrice: number;
  currentPrice: number;
  invested: number;
  currentValue: number;
  unrealizedPnL: number;
  realizedPnL: number;
  totalPnL: number;
  pnlPercent: number;
  isOpen: boolean;
  /** 'open' | 'closed' | 'settled' — settled = can't sell, only redeem. */
  marketStatus?: "open" | "closed" | "resolved" | "settled" | "determined" | "amended" | "disputed" | "finalized";
  /** Resolved winner outcome label (populated for settled markets). */
  winningOutcome?: string;
  leaderId?: string | null;
  leaderName?: string | null;
  leaderImage?: string | null;
  /** Nº de compras ejecutadas que formaron el avg (badge overlay). null/≤1 → sin badge. */
  buyCount?: number | null;
  /** First trade timestamp for this (user, tokenId). Used by Date sort.
   *  Optional because legacy DB rows + Hyperliquid haven't backfilled. */
  openedAt?: string | null;
  /** Position close timestamp (size→0). NULL while open. Used by Closed
   *  tab's Date sort. */
  closedAt?: string | null;
}

// Dev-only mock positions for previewing the column layout when running
// locally without real trade history. Inject with `?mockPositions=1`.
// Gated on NODE_ENV so the helper has zero effect in production.
function buildMockPositions(): Position[] {
  const mkRow = (i: number, over: Partial<Position>): Position => ({
    conditionId: `mock-cond-${i}`,
    tokenId: `mock-tok-${i}`,
    marketSlug: `mock-slug-${i}`,
    eventSlug: null,
    isMultiOutcome: false,
    marketQuestion: `Mock market ${i + 1} — will something interesting happen on May ${20 + i}?`,
    marketImage: undefined,
    outcome: i % 2 === 0 ? 'Yes' : 'No',
    outcomeIndex: i % 2 === 0 ? 0 : 1,
    shares: 0,
    avgPrice: 0,
    currentPrice: 0,
    invested: 0,
    currentValue: 0,
    unrealizedPnL: 0,
    realizedPnL: 0,
    totalPnL: 0,
    pnlPercent: 0,
    isOpen: true,
    ...over,
  });
  return [
    // Active winning position
    mkRow(0, { shares: 27.7, avgPrice: 0.18, currentPrice: 0.32, invested: 4.99, currentValue: 8.86, unrealizedPnL: 3.87, totalPnL: 3.87, pnlPercent: 77.5, isOpen: true, leaderName: 'pedrobueno', leaderImage: null }),
    // Active losing position
    mkRow(1, { shares: 22.2, avgPrice: 0.45, currentPrice: 0.28, invested: 9.99, currentValue: 6.22, unrealizedPnL: -3.77, totalPnL: -3.77, pnlPercent: -37.8, isOpen: true }),
    // Closed (sold out at profit)
    mkRow(2, { shares: 0, avgPrice: 0.20, currentPrice: 0, invested: 5.00, currentValue: 0, unrealizedPnL: 0, realizedPnL: 8.50, totalPnL: 8.50, pnlPercent: 170.0, isOpen: false, closedAt: new Date(Date.now() - 3600_000).toISOString() }),
    // Settled — WON
    mkRow(3, { shares: 16.6, avgPrice: 0.30, currentPrice: 1.00, invested: 4.98, currentValue: 16.60, unrealizedPnL: 11.62, totalPnL: 11.62, pnlPercent: 233.3, isOpen: false, marketStatus: 'resolved', winningOutcome: 'No', outcome: 'No', outcomeIndex: 1 }),
    // Settled — LOST
    mkRow(4, { shares: 50, avgPrice: 0.20, currentPrice: 0, invested: 10.00, currentValue: 0, unrealizedPnL: -10.00, totalPnL: -10.00, pnlPercent: -100, isOpen: false, marketStatus: 'resolved', winningOutcome: 'No', outcome: 'Yes', outcomeIndex: 0 }),
  ];
}

interface PositionsSummary {
  totalInvested: number;
  totalCurrentValue: number;
  totalPnL: number;
  totalPositions: number;
}

interface UserProfile {
  username?: string;
  name?: string;
  image?: string;
}

function formatPercentFn(value: number) {
  const safe = Number(value || 0);
  const sign = safe >= 0 ? "+" : "";
  return `${sign}${safe.toFixed(1)}%`;
}

// Markets whose resolution is final — can't sell, only redeem.
const SETTLED_STATUSES = ['resolved', 'settled', 'determined', 'amended', 'finalized', 'disputed'];

/** Result of a closed position — drives the table badge AND the share card
 *  (so the PnL card stops showing live Value/To-win/Now once it's done).
 *  null while the position is still open. */
function getClosedResult(p: Position): 'won' | 'lost' | 'sold' | null {
  const closed = !p.isOpen || p.shares <= 0;
  if (!closed) return null;
  // The market resolved if it has a winner (winningOutcome populated) OR a
  // terminal status. winningOutcome is the strongest signal — it only exists
  // once settled, regardless of the exact status string. WON/LOST = the market
  // decided it; SOLD = only when the user exited by selling before resolution.
  const resolved = !!p.winningOutcome || SETTLED_STATUSES.includes(String(p.marketStatus || ''));
  if (resolved) {
    // WON/LOST by the sign of the REALIZED P&L — the exact number the closed
    // row shows. (totalPnL is unrealized/mark-to-market ≈ 0 once a position is
    // closed, so it gave "WON -$0.12": realized −0.12 but totalPnL 0 ≥ 0.)
    return (Number(p.realizedPnL) || 0) >= 0 ? 'won' : 'lost';
  }
  return 'sold';
}

// Sort fields available in the portfolio dropdown. The set actually shown
// is filtered per tab (SORT_OPTIONS below) so Closed-only fields like
// "Traded" or "Current Price" don't appear on the Active tab where they
// don't apply.
type SortField =
  | 'value'
  | 'pnl'
  | 'percent'
  | 'avgPrice'
  | 'currentPrice'
  | 'date'
  | 'alphabetical'
  | 'traded';

// Grid templates for the desktop positions table. Closed drops "Now"
// (current price) and "Value" (live position value) — both always render
// as "—" once a position is closed, so the columns add nothing. Closed
// re-balances the freed space into Bought + PnL since those two are the
// numbers that actually matter post-resolution.
// The market column keeps a `minmax(150px, …)` floor so it never collapses
// to a "Is…"-style 1-char truncation when the container is just over the
// grid breakpoint (the fixed columns + gaps already eat ~680px). The
// `@[860px]` gate below guarantees there's room for that floor before the
// grid renders at all — narrower containers fall back to the stacked card.
// Columns: icon · market · outcome · $ (bought) · shares · avg · [now · value] · pnl · actions.
const ACTIVE_GRID_COLS = '40px minmax(150px, 1fr) 90px 80px 64px 60px 60px 90px 80px 84px';
// Closed omits Now/Value (always "—" once closed) AND Shares/Avg (a finished
// position's share count + entry price add nothing — the result is the PnL +
// Won/Lost/Sold badge). Tracks: image | market | outcome | bought | pnl | actions.
const CLOSED_GRID_COLS = '40px minmax(150px, 1fr) 90px 100px 120px 84px';

// Dust threshold + rule live in ../dust (isActiveDust), shared with the
// per-source summaries and the TG bot so the list and the "N open" stat agree.

// Compact share count for the narrow Shares column: 27.7 → "27.7", 356 → "356",
// 25_500 → "25.5k", 1_200_000 → "1.2M". Strips trailing ".0" so whole numbers
// read clean.
function formatShares(n: number): string {
  if (!(n > 0)) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  if (n >= 100) return String(Math.round(n));
  return n.toFixed(1).replace(/\.0$/, '');
}

// Short label for the "via @<leader>" chip. When the leader has no real
// handle the backend hands us a raw wallet (optionally with a "-<leaderId>"
// suffix fallback, e.g. "0x6982049c…A165-1777135"). Rendered verbatim it
// blew past the chip and wrapped/overflowed in the positions table, so
// collapse any address-shaped value to `0x6982…A165` and hard-cap the rest.
function shortLeaderName(name?: string | null): string {
  if (!name) return "";
  const addr = name.match(/^(0x[0-9a-fA-F]{4})[0-9a-fA-F]+([0-9a-fA-F]{4})/);
  if (addr) return `${addr[1]}…${addr[2]}`;
  return name.length > 16 ? `${name.slice(0, 14)}…` : name;
}

const SORT_OPTIONS: Record<'open' | 'closed', SortField[]> = {
  // Mirrors Polymarket's Active sort: Profit/Loss, Average Price,
  // Alphabetically, Date. We also keep Value (default) and %.
  open: ['value', 'pnl', 'percent', 'avgPrice', 'date', 'alphabetical'],
  // Mirrors Polymarket's Closed sort: Value, Profit/Loss $, Profit/Loss %,
  // Traded, Alphabetically, Average Price, Current Price. We also keep
  // Date so users can sort by close time.
  closed: ['value', 'pnl', 'percent', 'traded', 'alphabetical', 'avgPrice', 'currentPrice', 'date'],
};

// Memoized position card - only re-renders when its own data/state changes
interface PositionCardProps {
  position: Position;
  isMenuOpen: boolean;
  hasMergePair: boolean;
  isClosed: boolean;
  onSell: () => void;
  onShare: () => void;
  onMerge: () => void;
  onNavigate: () => void;
  onToggleMenu: () => void;
  marketImage: string | null;
  source?: PositionSource;
  leaderName?: string | null;
  leaderImage?: string | null;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const PositionCard = memo(function PositionCard({
  position, isMenuOpen, hasMergePair, isClosed,
  onSell, onShare, onMerge, onNavigate, onToggleMenu, marketImage, source = "polymarket", leaderName, leaderImage, t,
}: PositionCardProps) {
  const isProfit = position.totalPnL >= 0;
  // Prefer the native Kalshi side label ("Trump", "Above 70°F") over the raw
  // "yes"/"no" enum — falls back cleanly for PM / Predict positions.
  const rawOutcome = position.outcome
    ? position.outcome.charAt(0).toUpperCase() + position.outcome.slice(1).toLowerCase()
    : '';
  // 2026-06-05: sports/esports moneyline — the two outcomes ARE the team
  // names (["GLYPH","REKONIX"]), so the binary Yes/No enum (and the
  // groupItemTitle "Game 2 Winner") can't tell us which SIDE the user backed.
  // Derive the team straight from the ordered outcomes pair (backend =
  // source of truth, same order as the market's outcomes), so the chip shows
  // the team you bet ("GLYPH") instead of the sub-market name. The sub-market
  // ("Game 2 Winner") then renders as a secondary line so multi-game events
  // stay distinguishable.
  const NON_TEAM_OUTCOME = /^(yes|no|over|under|up|down)$/i;
  const teamOutcomes =
    Array.isArray(position.outcomes) &&
    position.outcomes.length === 2 &&
    !NON_TEAM_OUTCOME.test((position.outcomes[0] || '').trim()) &&
    !NON_TEAM_OUTCOME.test((position.outcomes[1] || '').trim());
  const teamLabel = teamOutcomes ? position.outcomes![position.outcomeIndex] : null;
  // Sub-market line shown under the matchup title, only for team markets
  // (Game/Map/Series winner). Skipped when the title already equals it.
  const submarketLabel = teamLabel && position.groupItemTitle ? position.groupItemTitle : null;
  // 2026-05-26: for multi-outcome events use Polymarket's per-sub-market
  // variant label ("28°C", "82-83°F") instead of the binary Yes/No
  // outcome. Single-outcome positions keep their native or yes/no
  // label since there's no variant.
  const outcomeLabel = teamLabel
    ?? (position.isMultiOutcome && position.groupItemTitle
      ? position.groupItemTitle
      : position.outcomeNativeLabel
        || rawOutcome
        || (position.outcomeIndex === 0 ? 'Yes' : 'No'));
  const isPositiveOutcome = position.outcomeIndex === 0;
  const isCopytrading = !!leaderName;
  const isSettled = SETTLED_STATUSES.includes(String(position.marketStatus || ''));
  const didWin = isSettled && position.winningOutcome === position.outcome;
  // 2026-05-21: detect closed-out rows (shares==0, all sold or redeemed)
  // so we can render a leaner meta line without the "0.0 shares @ Xc |
  // Now Yc" stub that has nothing to say. The non-zero data we DO have
  // for these — invested + realizedPnL — surfaces in the new meta row.
  const isClosedRow = !position.isOpen || position.shares <= 0;
  const realizedPnl = Number(position.realizedPnL) || 0;
  const investedUsd = Number(position.invested) || (Number(position.shares) * Number(position.avgPrice));
  // Result badge for closed rows so the outcome reads at a glance:
  // Won/Lost when the market settled, Sold when the user exited by selling
  // (closed-out but the market never resolved).
  const resultBadge: { label: string; cls: string } | null = !isClosedRow
    ? null
    : isSettled
      ? (didWin
          ? { label: t("portfolio.positions.won"), cls: "bg-emerald-500/20 text-emerald-500" }
          : { label: t("portfolio.positions.lost"), cls: "bg-red-500/20 text-red-400" })
      : { label: t("portfolio.positions.sold"), cls: "bg-gray-100 dark:bg-[#25252B] text-gray-600 dark:text-gray-300" };
  // Settled markets can't be sold — only redeemed. Disable the sell CTA.
  const canSell = !isClosed && !isSettled && position.currentPrice > 0 && position.shares > 0;
  const imageCandidates = marketImage ? [marketImage] : [];
  const [imageIndex, setImageIndex] = useState(0);
  const imageSrc = imageCandidates[imageIndex] || null;

  useEffect(() => {
    setImageIndex(0);
  }, [source, marketImage, position.marketSlug]);

  return (
    <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] transition-colors duration-200">
      {/* Desktop Layout — column grid (2026-05-26).
          Grid template (column widths): 40px image | 1fr market | 90px
          outcome+badges | 80px bought | 60px avg | 60px now | 90px
          value | 110px pnl | 84px actions. The header row outside
          this card uses the SAME template so the values line up
          vertically across rows. */}
      <div
        className="hidden @[860px]:grid p-3 items-center gap-3"
        style={{ gridTemplateColumns: isClosed ? CLOSED_GRID_COLS : ACTIVE_GRID_COLS }}
      >
        <div className="relative w-10 h-10 flex-shrink-0 cursor-pointer" onClick={onNavigate}>
          <div className="w-full h-full rounded-[5px] overflow-hidden bg-gray-100 dark:bg-[#0f0f12]">
            {imageSrc ? (
              <img
                src={imageSrc}
                alt={position.marketQuestion || "Market position"}
                className="w-full h-full object-cover"
                onError={() => setImageIndex((idx) => idx + 1)}
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-100 dark:from-[#1A1A1A] dark:to-[#0f0f12] flex items-center justify-center">
                <span className={`text-xs font-bold ${isPositiveOutcome ? "text-emerald-500" : "text-red-400"}`}>{outcomeLabel.charAt(0).toUpperCase()}</span>
              </div>
            )}
          </div>
          {/* Platform icon — bottom-left */}
          <div className={`absolute -bottom-1 -left-1 w-4 h-4 rounded-[3px] flex items-center justify-center ${source === 'kalshi' ? 'bg-emerald-500 text-[8px] font-bold text-white' : source === 'predict' ? 'bg-[#8B5CF6] text-[8px] font-bold text-white' : source === 'hyperliquid' ? 'bg-[#0f1a1f]' : 'bg-[#3B82F6]'}`}>
            {source === 'kalshi' ? 'K' : source === 'predict' ? 'P' : source === 'hyperliquid' ? (
              <img src="/logos/hyperliquid/symbol-mint.svg" alt="HL" className="w-2.5 h-2.5" />
            ) : (
              <img src="/logos/polymarket/icon-white.svg" alt="PM" className="w-2.5 h-2.5" />
            )}
          </div>
          {/* Copytrading badge — bottom-right */}
          {isCopytrading && (
            <div
              className="absolute -bottom-1 -right-1 w-4 h-4 rounded-[3px] flex items-center justify-center bg-violet-500 cursor-help"
              title={t("portfolio.positions.copiedFrom", { name: leaderName || "" })}
            >
              <FiCopy className="w-2 h-2 text-white" />
            </div>
          )}
          {/* Badge contador — arriba-izquierda: nº de compras que formaron
              el avg. Solo si >1. Neutro (sin neón), borde del color de la card. */}
          {(position.buyCount ?? 0) > 1 && (
            <div
              className="absolute -top-1 -left-1 min-w-[16px] h-4 px-0.5 rounded-[3px] flex items-center justify-center bg-gray-900 dark:bg-white text-white dark:text-[#0A0A0A] text-[8px] font-bold border-2 border-white dark:border-[#18181D]"
              title={`${position.buyCount} buys ($${Number(position.invested || 0).toFixed(2)}) · avg ${formatCents(Number(position.avgPrice || 0), 2)}`}
            >
              {(position.buyCount ?? 0) > 9 ? "9+" : position.buyCount}
            </div>
          )}
        </div>
        {/* Col 2: Market title + subtitle + COPY chip */}
        <div className="min-w-0 cursor-pointer" onClick={onNavigate}>
          <h3 title={position.marketQuestion || undefined} className="text-xs font-medium text-gray-900 dark:text-white line-clamp-1 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">{marketDisplayTitle({ isMultiOutcome: position.isMultiOutcome, eventTitle: position.eventTitle, eventSlug: position.eventSlug, marketQuestion: position.marketQuestion, groupItemTitle: position.groupItemTitle, outcomes: position.outcomes }, position.marketQuestion || '')}</h3>
          {submarketLabel && (
            <p className="text-[10px] text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">{submarketLabel}</p>
          )}
          {position.marketSubtitle && (
            <p className="text-[10px] text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">{position.marketSubtitle}</p>
          )}
          {isCopytrading && (
            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
              <span className="inline-flex items-center gap-1 max-w-full min-w-0 px-1.5 py-0.5 rounded text-[10px] text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-[#25252B]">
                <span className="opacity-60 flex-shrink-0">{t("ct.exec.viaLabel")}</span>
                <LeaderAvatar src={leaderImage} address={leaderName || null} size={12} />
                <span className="font-medium truncate min-w-0" title={leaderName || undefined}>@{shortLeaderName(leaderName)}</span>
              </span>
            </div>
          )}
        </div>
        {/* Col 3: Outcome chip + Won/Lost when settled */}
        <div className="flex flex-col items-center gap-1 min-w-0">
          <span
            title={outcomeLabel}
            className={`inline-block max-w-full truncate px-1.5 py-0.5 rounded text-[10px] font-semibold ${
              isPositiveOutcome
                ? "bg-emerald-500/15 text-emerald-500"
                : "bg-red-500/15 text-red-400"
            }`}
          >
            {outcomeLabel}
          </span>
          {resultBadge && (
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${resultBadge.cls}`}>
              {resultBadge.label}
            </span>
          )}
        </div>
        {/* Col 4: Bought (invested $) */}
        <div className="text-center tabular-nums">
          <p className="text-xs font-medium text-gray-900 dark:text-white">
            {investedUsd > 0 ? formatCurrency(investedUsd) : '—'}
          </p>
        </div>
        {/* Cols 5 + 6: Shares held + Avg entry price. Hidden on Closed —
            a finished position's share count + entry price add no info
            (CLOSED_GRID_COLS omits both tracks). */}
        {!isClosed && (
          <>
            <div className="text-center tabular-nums">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {formatShares(Number(position.shares) || 0)}
              </p>
            </div>
            <div className="text-center tabular-nums">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {Number(position.avgPrice || 0) > 0 ? `${formatCents(Number(position.avgPrice), 2)}` : '—'}
              </p>
            </div>
          </>
        )}
        {/* Cols 6 + 7: Now (current price) + Value (live position value).
            Hidden in the Closed tab entirely — both always render as "—"
            for closed rows so the columns add no information. The grid
            template above uses CLOSED_GRID_COLS in that case, which omits
            these two tracks. */}
        {!isClosed && (
          <>
            <div className="text-center tabular-nums">
              <p className="text-xs text-gray-900 dark:text-white">
                {isClosedRow ? '—' : `${formatCents(Number(position.currentPrice || 0), 2)}`}
              </p>
            </div>
            <div className="text-center tabular-nums">
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {isClosedRow ? '—' : formatCurrency(position.currentValue)}
              </p>
            </div>
          </>
        )}
        {/* Col 8: PnL — 2 lines so the column stays narrow. Line 1:
            $ amount. Line 2: (%) muted. Closed rows skip the percent
            (we don't show pnlPercent for realized in this view). */}
        <div className="text-center tabular-nums leading-tight">
          {isClosedRow ? (
            <p className={`text-sm font-semibold ${realizedPnl >= 0 ? "text-emerald-500" : "text-red-400"}`}>
              {realizedPnl >= 0 ? "+" : ""}{formatPnL(realizedPnl)}
            </p>
          ) : (
            <>
              <p className={`text-sm font-semibold ${isProfit ? "text-emerald-500" : "text-red-400"}`}>
                {isProfit ? "+" : ""}{formatPnL(position.totalPnL)}
              </p>
              <p className={`text-[10px] ${isProfit ? "text-emerald-500/70" : "text-red-400/70"}`}>
                {formatPercentFn(position.pnlPercent)}
              </p>
            </>
          )}
        </div>
        {/* Col 9: Actions */}
        <div className="flex items-center justify-end gap-1">
          {canSell && (
            <button onClick={onSell} className="px-2.5 py-1.5 rounded-[5px] text-[10px] font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors [text-shadow:_0_1px_2px_rgba(0,0,0,0.45)]">{t("portfolio.positions.sell")}</button>
          )}
          <div className="relative">
            <button onClick={(e) => { e.stopPropagation(); onToggleMenu(); }} className="p-1.5 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
              <FiMoreVertical className="w-3.5 h-3.5" />
            </button>
            <AnimatePresence>
              {isMenuOpen && (
                <motion.div initial={{ opacity: 0, y: -8, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: 0.95 }} transition={{ duration: 0.15 }} className="absolute right-0 top-full mt-1 bg-white dark:bg-[#0f0f12] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] shadow-xl z-20 py-1 w-24">
                  <button onClick={onShare} className="w-full px-3 py-2 text-left text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#1A1A1A]/50 flex items-center gap-2 transition-colors"><FiShare2 className="w-3.5 h-3.5" /> {t("portfolio.positions.share")}</button>
                  <button onClick={onNavigate} className="w-full px-3 py-2 text-left text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#1A1A1A]/50 flex items-center gap-2 transition-colors"><FiExternalLink className="w-3.5 h-3.5" /> {t("portfolio.positions.view")}</button>
                  {!isClosed && hasMergePair && (
                    <button onClick={onMerge} className="w-full px-3 py-2 text-left text-xs text-emerald-500 hover:bg-emerald-500/10 flex items-center gap-2 transition-colors"><FiRefreshCw className="w-3.5 h-3.5" /> {t("portfolio.positions.merge")}</button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
      {/* Mobile Layout — 2026-06-04 redesign. items-start pins the value
          column to the top so it doesn't float against tall content. The
          meta below the title is split into fixed-role lines (outcome /
          numbers / leader) with truncation instead of one big flex-wrap,
          so card height stays constant and nothing reflows row-to-row. */}
      <div className="@[860px]:hidden p-3">
        <div className="flex items-start gap-3" onClick={onNavigate}>
          <div className="relative w-12 h-12 flex-shrink-0">
            <div className="w-full h-full rounded-[5px] overflow-hidden bg-gray-100 dark:bg-[#0f0f12]">
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt={position.marketQuestion || "Market position"}
                  className="w-full h-full object-cover"
                  onError={() => setImageIndex((idx) => idx + 1)}
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-100 dark:from-[#1A1A1A] dark:to-[#0f0f12] flex items-center justify-center">
                  <span className={`text-sm font-bold ${isPositiveOutcome ? "text-emerald-500" : "text-red-400"}`}>{outcomeLabel.charAt(0).toUpperCase()}</span>
                </div>
              )}
            </div>
            {/* Platform icon — bottom-left */}
            <div className={`absolute -bottom-1 -left-1 w-5 h-5 rounded-[3px] flex items-center justify-center ${source === 'kalshi' ? 'bg-emerald-500 text-[9px] font-bold text-white' : source === 'predict' ? 'bg-[#8B5CF6] text-[9px] font-bold text-white' : source === 'hyperliquid' ? 'bg-[#0f1a1f]' : 'bg-[#3B82F6]'}`}>
              {source === 'kalshi' ? 'K' : source === 'predict' ? 'P' : source === 'hyperliquid' ? (
                <img src="/logos/hyperliquid/symbol-mint.svg" alt="HL" className="w-3 h-3" />
              ) : (
                <img src="/logos/polymarket/icon-white.svg" alt="PM" className="w-3 h-3" />
              )}
            </div>
            {/* Copytrading badge — bottom-right */}
            {isCopytrading && (
              <div
                className="absolute -bottom-1 -right-1 w-5 h-5 rounded-[3px] flex items-center justify-center bg-violet-500 cursor-help"
                title={t("portfolio.positions.copiedFrom", { name: leaderName || "" })}
              >
                <FiCopy className="w-2.5 h-2.5 text-white" />
              </div>
            )}
            {/* Badge contador — arriba-izquierda (mobile). */}
            {(position.buyCount ?? 0) > 1 && (
              <div
                className="absolute -top-1 -left-1 min-w-[18px] h-[18px] px-1 rounded-[3px] flex items-center justify-center bg-gray-900 dark:bg-white text-white dark:text-[#0A0A0A] text-[9px] font-bold border-2 border-white dark:border-[#18181D]"
                title={`${position.buyCount} buys ($${Number(position.invested || 0).toFixed(2)}) · avg ${formatCents(Number(position.avgPrice || 0), 2)}`}
              >
                {(position.buyCount ?? 0) > 9 ? "9+" : position.buyCount}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 title={position.marketQuestion || undefined} className="text-sm font-medium text-gray-900 dark:text-white line-clamp-1 leading-tight">{marketDisplayTitle({ isMultiOutcome: position.isMultiOutcome, eventTitle: position.eventTitle, eventSlug: position.eventSlug, marketQuestion: position.marketQuestion, groupItemTitle: position.groupItemTitle, outcomes: position.outcomes }, position.marketQuestion || '')}</h3>
            {submarketLabel && (
              <p className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">{submarketLabel}</p>
            )}
            {position.marketSubtitle && (
              <p className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">{position.marketSubtitle}</p>
            )}
            {/* Línea 2 — outcome (+ resultado si está cerrada). Chip TRUNCADO
                y sin wrap: las etiquetas largas de deportes/handicap ya no
                explotan a varias líneas → altura de card constante. */}
            <div className="flex items-center gap-1.5 mt-1 min-w-0">
              {isClosedRow && resultBadge && (
                <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold ${resultBadge.cls}`}>
                  {resultBadge.label}
                </span>
              )}
              <span
                title={outcomeLabel}
                className={`min-w-0 truncate px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                  isPositiveOutcome
                    ? "bg-emerald-500/15 text-emerald-500"
                    : "bg-red-500/15 text-red-400"
                }`}
              >
                {outcomeLabel}
              </span>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="font-bold text-sm text-gray-900 dark:text-white">{formatCurrency(position.currentValue)}</p>
            <p className={`text-[11px] font-medium flex items-center justify-end gap-0.5 ${isProfit ? "text-emerald-500" : "text-red-400"}`}>
              {isProfit ? <FiTrendingUp className="w-3 h-3" /> : <FiTrendingDown className="w-3 h-3" />}
              {isProfit ? "+" : ""}{formatPnL(position.totalPnL)}
              <span className="opacity-70">({formatPercentFn(position.pnlPercent)})</span>
            </p>
          </div>
        </div>
        {/* Detalle a ANCHO COMPLETO, debajo de la imagen del evento
            (2026-06-04, a petición): números de la compra + leader copiado.
            Antes iban en la columna del título; ahora ocupan su propia línea
            bajo la card para que el bloque superior respire. */}
        <div onClick={onNavigate} className="mt-2">
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0 text-[11px] text-gray-500 dark:text-gray-400 truncate">
            {isClosedRow ? (
              <>
                {investedUsd > 0 && <>Bought {formatCurrency(investedUsd)} · </>}
                <span className={realizedPnl >= 0 ? "text-emerald-500" : "text-red-400"}>
                  Realized {realizedPnl >= 0 ? "+" : ""}{formatPnL(realizedPnl)}
                </span>
              </>
            ) : (
              <>
                {Number(position.shares || 0) > 0 && Number(position.avgPrice || 0) > 0 && (
                  <>Bought {formatCurrency(Number(position.shares) * Number(position.avgPrice))} · </>
                )}
                {Number(position.shares || 0).toFixed(1)} @ {Number(position.avgPrice || 0) > 0 ? `${formatCents(Number(position.avgPrice), 2)}` : "—"}
                {" · "}
                <span className="text-gray-900 dark:text-white">Now {formatCents(Number(position.currentPrice || 0), 2)}</span>
              </>
            )}
            </div>
            {/* Share — same line as the Bought/Realized detail, pinned right
                and compact (moved here from the footer 2026-06-09). */}
            <button
              onClick={(e) => { e.stopPropagation(); onShare(); }}
              className="flex-shrink-0 p-1 rounded-[4px] text-gray-400 hover:text-gray-900 dark:text-gray-500 dark:hover:text-white transition-colors"
              title={t("portfolio.positions.share")}
            >
              <FiShare2 className="w-3.5 h-3.5" />
            </button>
          </div>
          {isCopytrading && (
            <div className="mt-1">
              <span className="inline-flex items-center gap-1 max-w-full min-w-0 px-1.5 py-0.5 rounded text-[10px] text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-[#25252B]">
                <span className="opacity-60 flex-shrink-0">{t("ct.exec.viaLabel")}</span>
                <LeaderAvatar src={leaderImage} address={leaderName || null} size={12} />
                <span className="font-medium truncate min-w-0" title={leaderName || undefined}>@{shortLeaderName(leaderName)}</span>
              </span>
            </div>
          )}
        </div>
        {/* Footer only for the action button(s) on OPEN positions. Share moved
            up to the Bought/Realized line; the external-link button was removed
            (card is tappable). Closed rows render no footer (no empty border). */}
        {canSell && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100 dark:border-[#1A1A1A]/50">
            <button onClick={(e) => { e.stopPropagation(); onSell(); }} className="flex-1 py-2 rounded-[5px] text-xs font-semibold bg-red-500 text-white hover:bg-red-600 transition-colors active:scale-[0.98] [text-shadow:_0_1px_2px_rgba(0,0,0,0.45)]">{t("portfolio.positions.sell")}</button>
            {hasMergePair && (
              <button onClick={(e) => { e.stopPropagation(); onMerge(); }} className="p-2 rounded-[5px] bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 transition-colors" title={t("portfolio.positions.mergeYesNo")}><FiRefreshCw className="w-4 h-4" /></button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}, (prev, next) => {
  return prev.position === next.position
    && prev.isMenuOpen === next.isMenuOpen
    && prev.hasMergePair === next.hasMergePair
    && prev.isClosed === next.isClosed
    && prev.marketImage === next.marketImage
    && prev.source === next.source;
});

interface PositionsSectionProps {
  /** Unified positions from useUnifiedPositions hook */
  unifiedPositions?: UnifiedPosition[];
  /** Loading state from parent */
  externalLoading?: boolean;
  /** Error from parent */
  externalError?: string | null;
  /** Refetch callback */
  onRefetch?: () => void;
}

export default function PositionsSection({
  unifiedPositions,
  externalLoading,
  externalError,
  onRefetch,
}: PositionsSectionProps) {
  const t = useT();
  const router = useRouter();
  const { user, isLoggedIn } = useAuth();
  // Dev-only mock positions toggle. `?mockPositions=1` on localhost
  // seeds the state with 5 representative rows (active winning, active
  // losing, closed, settled-won, settled-lost) so the column layout
  // can be previewed without trade history. Production builds gate
  // this off via NODE_ENV.
  const isMockPositions = (() => {
    if (typeof window === 'undefined') return false;
    if (process.env.NODE_ENV !== 'development') return false;
    return new URLSearchParams(window.location.search).get('mockPositions') === '1';
  })();
  const [positions, setPositions] = useState<Position[]>(
    isMockPositions ? buildMockPositions() : [],
  );
  const [summary, setSummary] = useState<PositionsSummary | null>(null);
  const { data: redeemableData } = useRedeemableQuery();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters & sort. SortField mirrors Polymarket's portfolio dropdown so
  // copy-trading users coming from Polymarket find familiar controls.
  // Available options differ between Active and Closed — see SORT_OPTIONS
  // map below. Default = 'value' for both tabs (matches Polymarket default).
  const [statusFilter, setStatusFilter] = useState<'open' | 'closed'>('open');
  const [sourceFilter, setSourceFilter] = useState<'all' | PositionSource>('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Paginación del tab Closed (un trader activo acumula decenas de cerradas;
  // mostrarlas todas alarga la página y monta muchos nodos). Active no pagina
  // (pocas abiertas). 15/página.
  const [closedPage, setClosedPage] = useState(0);
  const [sortBy, setSortBy] = useState<SortField>('value');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const sortMenuRef = useRef<HTMLDivElement>(null);

  // Modals
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);
  const [selectedUnified, setSelectedUnified] = useState<UnifiedPosition | null>(null);
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);

  // Merge
  const [mergeModalOpen, setMergeModalOpen] = useState(false);

  // Manual refresh — dispatches the same event the useUnifiedPositions hook
  // listens for (immediate refetch + 1.5s/4s burst). Spins briefly for feedback.
  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = () => {
    if (refreshing) return;
    setRefreshing(true);
    window.dispatchEvent(new Event("positions:refresh"));
    window.setTimeout(() => setRefreshing(false), 800);
  };

  // Sell All
  const [sellAllModalOpen, setSellAllModalOpen] = useState(false);
  const [sellAllLoading, setSellAllLoading] = useState(false);
  const [sellAllProgress, setSellAllProgress] = useState({ current: 0, total: 0, success: 0, failed: 0 });
  const [sellAllComplete, setSellAllComplete] = useState(false);
  const sellAllAbortRef = useRef<AbortController | null>(null);

  // Map to track source per position key
  const [positionSourceMap, setPositionSourceMap] = useState<Map<string, UnifiedPosition>>(new Map());

  // When unified positions arrive, convert to internal Position type
  useEffect(() => {
    if (!unifiedPositions) return;

    // Build source map
    const sourceMap = new Map<string, UnifiedPosition>();

    const mapped: Position[] = unifiedPositions
      .filter(up => {
        // Apply source filter
        if (sourceFilter !== 'all' && up.source !== sourceFilter) return false;
        // Apply status filter (Kalshi only has open)
        if (statusFilter === 'closed' && up.source === 'kalshi') return false;
        if (statusFilter === 'closed' && up.isOpen) return false;
        if (statusFilter === 'open' && !up.isOpen) return false;
        // Hide dust on Active (CLOB "sell all" leaves a fractional residue;
        // sub-$1 leftovers can't be re-sold at Polymarket's ~$1 min order).
        // Single source of truth in ../dust (shared with the per-source
        // summaries + the TG bot), so the list and the "N open" stat agree.
        // Dust re-surfaces on Closed via the !isOpen path once it resolves.
        if (statusFilter === 'open' && isActiveDust(up)) return false;
        // Closed tab hides ONLY structural rounding dust (a <1-share sell-all
        // leftover with ≈$0 everywhere). Every real finished position — any
        // sale, win or loss, even sub-$1 — stays, because the tab's title
        // promises "sold, won or lost". See isClosedDust.
        if (statusFilter === 'closed' && isClosedDust(up)) return false;
        return true;
      })
      .map(up => {
        const key = up.id;
        sourceMap.set(key, up);

        return {
          conditionId: up.conditionId || up.id,
          tokenId: up.tokenId || up.id,
          marketSlug: up.marketSlug || up.kalshiTicker || '',
          marketQuestion: up.marketTitle,
          marketSubtitle: up.marketSubtitle,
          marketImage: up.marketImage || undefined,
          outcome: up.outcome,
          outcomeIndex: up.outcomeIndex,
          groupItemTitle: (up as any).groupItemTitle ?? null,
          eventSlug: (up as any).eventSlug ?? null,
          isMultiOutcome: (up as any).isMultiOutcome ?? undefined,
          eventTitle: (up as any).eventTitle ?? null,
          outcomes: (up as any).outcomes ?? null,
          shares: up.shares,
          avgPrice: up.avgPrice,
          currentPrice: up.currentPrice,
          invested: up.invested,
          currentValue: up.currentValue,
          unrealizedPnL: up.unrealizedPnL || up.totalPnL,
          realizedPnL: up.realizedPnL || 0,
          totalPnL: up.totalPnL,
          pnlPercent: up.pnlPercent,
          isOpen: up.isOpen,
          marketStatus: up.marketStatus as Position["marketStatus"],
          winningOutcome: up.winningOutcome ?? undefined,
          leaderName: up.leaderName,
          leaderImage: (up as any).leaderImage,
          buyCount: (up as any).buyCount ?? null,
          openedAt: up.openedAt ?? null,
          closedAt: up.closedAt ?? null,
        };
      });

    setPositionSourceMap(sourceMap);
    // Dev preview: keep the mock rows when ?mockPositions=1 is set
    // (the unifiedPositions feed is empty for users without history).
    if (!isMockPositions) {
      setPositions(mapped);
    }

    // Compute summary
    const totalInvested = mapped.reduce((s, p) => s + p.invested, 0);
    const totalCurrentValue = mapped.reduce((s, p) => s + p.currentValue, 0);
    const totalPnL = mapped.reduce((s, p) => s + p.totalPnL, 0);
    setSummary({ totalInvested, totalCurrentValue, totalPnL, totalPositions: mapped.length });

    // In parent-owned mode (externalLoading provided by useUnifiedPositions),
    // the parent's loading flag is the source of truth — don't flip loading→
    // false here. unifiedPositions starts as [] during auth/Privy init, and
    // dropping the skeleton on that empty seed flashed "No active positions"
    // for ~1s on refresh. The externalLoading effect below owns it instead.
    if (externalLoading === undefined) setLoading(false);
  }, [unifiedPositions, sourceFilter, statusFilter, isMockPositions, externalLoading]);

  // Set loading from parent
  useEffect(() => {
    if (externalLoading !== undefined) setLoading(externalLoading);
  }, [externalLoading]);

  useEffect(() => {
    if (externalError) setError(externalError);
  }, [externalError]);

  const fetchPositions = async () => {
    // If using unified positions, delegate to parent
    if (onRefetch) {
      onRefetch();
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch positions and user profile in parallel
      const [positionsRes, profileRes] = await Promise.all([
        authFetch(`/api/portfolio/positions?status=${statusFilter}`),
        authFetch("/api/user/profile"),
      ]);

      if (!positionsRes.ok) throw new Error("Failed to fetch positions");

      const data = await positionsRes.json();
      // Apply the SAME dust rule as the unified path (single source of truth in
      // ../dust) so both render paths agree. Without this, the internal-fetch
      // fallback painted the backend's unfiltered sub-$1 residue that the
      // unified list hides. Recompute the summary from the filtered set so the
      // list and the totals/"N open" stat don't disagree. 2026-06-10.
      const rawPositions: Position[] = data.positions || [];
      const filteredPositions = rawPositions.filter((p) => {
        if (statusFilter === 'open' && isActiveDust(p)) return false;
        if (statusFilter === 'closed' && isClosedDust(p)) return false;
        return true;
      });
      setPositions(filteredPositions);
      setSummary({
        totalInvested: filteredPositions.reduce((s, p) => s + (Number(p.invested) || 0), 0),
        totalCurrentValue: filteredPositions.reduce((s, p) => s + (Number(p.currentValue) || 0), 0),
        totalPnL: filteredPositions.reduce((s, p) => s + (Number(p.totalPnL) || 0), 0),
        totalPositions: filteredPositions.length,
      });

      // Handle user profile for sharing
      if (profileRes.ok) {
        const profileData = await profileRes.json();
        if (profileData.user) {
          setUserProfile({
            username: profileData.user.username,
            name: profileData.user.name,
            image: profileData.user.image,
          });
        }
      }
    } catch (err) {
      logger.error("Error fetching positions", { error: err instanceof Error ? err.message : String(err) });
      setError(t("portfolio.positions.error"));
    } finally {
      setLoading(false);
    }
  };

  // Only fetch internally if NOT using unified positions
  useEffect(() => {
    if (unifiedPositions) return; // Using unified positions from parent
    if (isLoggedIn) {
      fetchPositions();
    } else {
      setLoading(false);
    }
  }, [isLoggedIn, statusFilter]);

  // Refresh on copy trade / position resolved / manual sell SSE events.
  // Retry schedule (immediate + 1.5s + 4s) absorbs PM data-api indexing
  // lag — without it, a manual sell that hit the trade endpoint, wrote
  // to DB and cleared the Redis cache could still surface stale on the
  // first refetch if PM's data-api hadn't propagated yet, and the user
  // would see the just-sold position in the list until the next 30-60s
  // passive poll. Same pattern `PolymarketBalanceContext` uses for the
  // header USDC chip.
  useEffect(() => {
    if (!isLoggedIn) return;
    // When the parent owns the data (unified mode on /portfolio), it
    // already registers its own trade-event listener with the `?fresh=1`
    // bypass. Re-running the same fetch from here in parallel races with
    // the parent's fresh fetch — the cached response from this side can
    // land *after* the fresh one and overwrite the just-updated state
    // with the pre-trade snapshot. Skip when delegating.
    if (onRefetch) return;
    const refreshTimeouts = new Set<ReturnType<typeof setTimeout>>();
    const refetchOnce = () => fetchPositions();
    const handleCopyTradeRefresh = () => {
      refetchOnce();
      [1500, 4000].forEach((delay) => {
        const id = setTimeout(() => {
          refreshTimeouts.delete(id);
          refetchOnce();
        }, delay);
        refreshTimeouts.add(id);
      });
    };
    window.addEventListener("copytrade:refresh", handleCopyTradeRefresh);
    window.addEventListener("portfolio:refresh", handleCopyTradeRefresh);
    window.addEventListener("positions:refresh", handleCopyTradeRefresh);
    return () => {
      window.removeEventListener("copytrade:refresh", handleCopyTradeRefresh);
      window.removeEventListener("portfolio:refresh", handleCopyTradeRefresh);
      window.removeEventListener("positions:refresh", handleCopyTradeRefresh);
      refreshTimeouts.forEach((id) => clearTimeout(id));
    };
  }, [isLoggedIn, onRefetch]);

  // Helper: get unified position data for a position key
  const getUnifiedPosition = useCallback((position: Position): UnifiedPosition | undefined => {
    for (const [key, up] of positionSourceMap) {
      // Exact key match (works for all sources — key = up.id)
      if (key === position.conditionId || key === position.tokenId) return up;
      // Polymarket match
      if (up.conditionId && up.tokenId && up.conditionId === position.conditionId && up.tokenId === position.tokenId) return up;
      if (up.id === `pm-${position.conditionId}-${position.tokenId}`) return up;
      // Kalshi match
      if (up.kalshiTicker && up.kalshiTicker === position.marketSlug) return up;
    }
    return undefined;
  }, [positionSourceMap]);

  // Helper: get source for a position.
  // Prefer the source stamped on the position itself — the HL portfolio
  // fan-out marks each row with `source: 'hyperliquid'` but those rows
  // never enter the unifiedPositions map (which is PM/Kalshi/Predict
  // only), so falling back to up?.source defaulted them to 'polymarket'
  // and routed every HL action through the PM endpoint (the
  // "Sell: Market not found" 404 + /polymarket/event/<hl-slug> link bug).
  const getPositionSource = useCallback((position: Position): PositionSource => {
    const stamped = (position as any)?.source as PositionSource | undefined;
    if (stamped) return stamped;
    const up = getUnifiedPosition(position);
    return up?.source || "polymarket";
  }, [getUnifiedPosition]);

  // Detect mergeable pairs (YES + NO on same market)
  const mergeablePairs = useMemo(() => {
    const byCondition: Record<string, Position[]> = {};
    positions.forEach(p => {
      if (!byCondition[p.conditionId]) byCondition[p.conditionId] = [];
      byCondition[p.conditionId].push(p);
    });
    const pairs: Record<string, { yes: Position; no: Position; maxMerge: number }> = {};
    Object.entries(byCondition).forEach(([cid, poses]) => {
      const yes = poses.find(p => p.outcomeIndex === 0);
      const no = poses.find(p => p.outcomeIndex === 1);
      if (yes && no) {
        pairs[cid] = { yes, no, maxMerge: Math.min(yes.shares, no.shares) };
      }
    });
    return pairs;
  }, [positions]);

  // Filtered + sorted positions
  const filteredPositions = useMemo(() => {
    let result = positions;

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(p =>
        p.marketQuestion.toLowerCase().includes(q) ||
        p.outcome.toLowerCase().includes(q)
      );
    }

    // Sort. Each field maps to a numeric (or string) comparator. For
    // 'date' we use opened_at on Active, closed_at on Closed — falling
    // back to opened_at when the close timestamp is missing so legacy
    // pre-tracking rows still rank somewhere instead of all clustering
    // at the bottom as 0. Alphabetical uses localeCompare so accented
    // titles ("Brésil") sort next to their unaccented peers.
    const dateAccessor = (p: Position): number => {
      const raw = statusFilter === 'closed'
        ? (p.closedAt || p.openedAt)
        : p.openedAt;
      if (!raw) return 0;
      const ms = new Date(raw).getTime();
      return Number.isFinite(ms) ? ms : 0;
    };
    // Defensive numeric coercion: positions arriving from the API can
    // have undefined / null for currentValue, currentPrice, etc. when
    // the price feed hasn't filled (Kalshi pre-WS, just-loaded PM).
    // `undefined - n` produces NaN, which makes Array.sort's behaviour
    // undefined and visually looks like "everything inverted" because
    // the runtime falls back to insertion order for the NaN rows.
    const n = (v: unknown): number => {
      const num = Number(v);
      return Number.isFinite(num) ? num : 0;
    };
    result = [...result].sort((a, b) => {
      let diff = 0;
      switch (sortBy) {
        case 'value': diff = n(a.currentValue) - n(b.currentValue); break;
        case 'pnl': diff = n(a.totalPnL) - n(b.totalPnL); break;
        case 'percent': diff = n(a.pnlPercent) - n(b.pnlPercent); break;
        case 'avgPrice': diff = n(a.avgPrice) - n(b.avgPrice); break;
        case 'currentPrice': diff = n(a.currentPrice) - n(b.currentPrice); break;
        case 'traded': diff = n(a.invested) - n(b.invested); break;
        case 'date': diff = dateAccessor(a) - dateAccessor(b); break;
        case 'alphabetical':
          diff = (a.marketQuestion || '').localeCompare(b.marketQuestion || '', undefined, { sensitivity: 'base' });
          break;
      }
      return sortDir === 'desc' ? -diff : diff;
    });

    return result;
  }, [positions, searchQuery, sortBy, sortDir, statusFilter]);

  // Reset la paginación de Closed cuando cambia el set mostrado (tab, búsqueda,
  // orden) — si no, el usuario puede quedar en una página vacía.
  useEffect(() => { setClosedPage(0); }, [statusFilter, searchQuery, sortBy, sortDir]);

  // Close sort dropdown on outside-click or Escape. Mounted only while
  // the menu is open so we don't bind global listeners for nothing.
  useEffect(() => {
    if (!sortMenuOpen) return;
    const onPointer = (e: MouseEvent) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(e.target as Node)) {
        setSortMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSortMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [sortMenuOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClick = () => setExpandedMenu(null);
    if (expandedMenu) {
      document.addEventListener("click", handleClick);
      return () => document.removeEventListener("click", handleClick);
    }
  }, [expandedMenu]);

  const formatPercent = formatPercentFn;

  const handleTabChange = (tab: 'open' | 'closed') => {
    if (tab !== statusFilter) {
      setStatusFilter(tab);
      setSearchQuery('');
      setExpandedMenu(null);
      // Reset sort if the active field doesn't exist on the new tab
      // (e.g. switching from Closed's "Traded" → Active where Traded
      // isn't listed). Default back to Value to match initial state.
      if (!SORT_OPTIONS[tab].includes(sortBy)) {
        setSortBy('value');
        setSortDir('desc');
      }
      setSortMenuOpen(false);
    }
  };

  // Click on the active option toggles asc/desc (Polymarket-style); click
  // on a different option selects it with a sensible default direction.
  // We deliberately KEEP the menu open after a click so users can flip the
  // direction in place without reopening — closing happens via outside-click
  // or pressing Escape.
  const handleSortClick = (field: SortField) => {
    if (sortBy === field) {
      setSortDir(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      // Alphabetical defaults to ascending (A→Z reads naturally); every
      // other field defaults to descending so the biggest value lands at
      // the top, matching what users expect from a "Sort by Value" click.
      setSortDir(field === 'alphabetical' ? 'asc' : 'desc');
    }
  };

  const handleMerge = (position: Position) => {
    setSelectedPosition(position);
    setMergeModalOpen(true);
    setExpandedMenu(null);
  };

  const tradingModal = useTradingModalStore();

  const handleSell = (position: Position) => {
    const source = getPositionSource(position);
    const unified = getUnifiedPosition(position);
    setSelectedPosition(position);
    setExpandedMenu(null);
    setSelectedUnified(unified || null);

    if (source === "kalshi" && unified) {
      tradingModal.openKalshi({
        marketTicker: unified.kalshiTicker || '',
        dflowTicker: unified.dflowTicker || undefined,
        marketTitle: unified.marketTitle,
        outcome: unified.outcome as "yes" | "no",
        currentPrice: unified.currentPrice,
        yesMint: unified.yesMint || undefined,
        noMint: unified.noMint || undefined,
        mode: "sell",
        positionSize: unified.shares,
        avgEntryPrice: unified.avgPrice,
      });
    } else if (source === "predict" && unified) {
      tradingModal.openPredict({
        marketId: unified.conditionId || unified.id,
        marketSlug: unified.conditionId || unified.id,
        outcome: unified.outcomeIndex === 0 ? "YES" : "NO",
        outcomePrice: unified.currentPrice || 0.5,
        marketQuestion: unified.marketTitle,
        marketImage: unified.marketImage,
        mode: "sell",
        positionShares: unified.shares,
        positionAvgPrice: unified.avgPrice,
      });
    } else if (source === "hyperliquid") {
      // HL: navigate to the event page instead of opening a modal.
      // The HL event page renders the full TradingPanel with depth /
      // recent trades / position context — better UX than a constrained
      // modal for outcome trades. Pass `?action=sell` so the page can
      // pre-select the SELL tab and pre-fill shares.
      const slug = position.marketSlug || '';
      const outcomeParam = position.outcomeIndex === 0 ? 'yes' : 'no';
      const sharesParam = encodeURIComponent(String(position.shares || ''));
      router.push(`/hyperliquid/event/${slug}?action=sell&outcome=${outcomeParam}&shares=${sharesParam}`);
    } else {
      // Polymarket uses the store too
      // Pass the FULL outcomes pair from the API so the modal/panel render
      // real labels (Over/Under, Up/Down, team names) — never reconstruct
      // the second side from "YES"/"NO" downstream.
      const outcomesPair: string[] | undefined =
        Array.isArray(position.outcomes) && position.outcomes.length === 2
          ? position.outcomes
          : (Array.isArray(unified?.outcomes) && unified!.outcomes!.length === 2
              ? (unified!.outcomes as string[])
              : undefined);
      const oppositeLabel = outcomesPair
        ? (outcomesPair[0] === position.outcome ? outcomesPair[1] : outcomesPair[0])
        : undefined;
      const altPrice = typeof position.currentPrice === 'number'
        ? Math.max(0, Math.min(1, 1 - position.currentPrice))
        : undefined;
      tradingModal.openPolymarket({
        marketSlug: position.marketSlug || '',
        outcome: position.outcomeIndex === 0 ? "YES" : "NO",
        outcomePrice: position.currentPrice || 0.5,
        altOutcomePrice: altPrice,
        marketQuestion: position.marketQuestion || unified?.marketTitle || '',
        marketImage: getMarketImage(position) || unified?.marketImage,
        actualOutcome: position.outcome,
        altActualOutcome: oppositeLabel,
        outcomes: outcomesPair,
        clobTokenIds: position.tokenId ? [position.tokenId] : undefined,
        conditionId: (position as any).conditionId,
        mode: "sell",
        positionShares: position.shares,
        positionAvgPrice: position.avgPrice,
      });
    }
  };

  const handleShare = (position: Position) => {
    setSelectedPosition(position);
    setShareModalOpen(true);
    setExpandedMenu(null);
  };

  const handleSellAllClick = () => {
    if (!isLoggedIn || positions.length === 0) return;
    setSellAllComplete(false);
    setSellAllProgress({ current: 0, total: positions.length, success: 0, failed: 0 });
    setSellAllModalOpen(true);
  };

  const executeSellAll = async () => {
    if (!isLoggedIn || positions.length === 0 || sellAllLoading) return;

    const abortController = new AbortController();
    sellAllAbortRef.current = abortController;

    setSellAllLoading(true);
    // Only sell positions with actual shares (skip closed/dust)
    const positionsSnapshot = positions.filter(p => p.shares > 0.01);

    setSellAllLoading(true);
    setSellAllProgress({ current: 0, total: positionsSnapshot.length, success: 0, failed: 0 });

    let successCount = 0;
    let failedCount = 0;

    for (let i = 0; i < positionsSnapshot.length; i++) {
      if (abortController.signal.aborted) break;

      const position = positionsSnapshot[i];
      const shares = position.shares;
      if (shares <= 0.001) continue;

      const source = getPositionSource(position);
      setSellAllProgress(prev => ({ ...prev, current: i + 1 }));

      try {
        let response: Response;

        if (source === 'kalshi') {
          // DFlow/Kalshi sell
          const up = getUnifiedPosition(position);
          response = await authFetch("/api/dflow/trade", {
            method: "POST",
            body: JSON.stringify({
              kalshiTicker: up?.kalshiTicker || position.marketSlug,
              dflowTicker: up?.dflowTicker,
              marketTitle: position.marketQuestion,
              action: "sell",
              outcome: position.outcome?.toLowerCase() || "yes",
              shares,
              yesMint: up?.yesMint,
              noMint: up?.noMint,
            }),
          });
        } else if (source === 'hyperliquid') {
          // HL sell — backend derives outcomeIndex/side from the assetId
          // (tokenId on HL rows is the asset_id, not a CTF id).
          const assetId = Number(position.tokenId);
          const outcomeIndex = assetId >= 100_000_000 ? Math.floor((assetId - 100_000_000) / 10) : 0;
          response = await authFetch("/api/trading/hyperliquid/execute", {
            method: "POST",
            body: JSON.stringify({
              outcomeIndex,
              side: position.outcomeIndex === 0 ? 'YES' : 'NO',
              tradeSide: 'SELL',
              size: shares,
              limitPrice: Math.max(0.001, Math.min(0.999, (position.currentPrice || 0.5) * 0.95)),
              tif: 'Ioc',
              surface: 'web',
            }),
          });
        } else if (source === 'predict') {
          // Predict.fun sell
          response = await authFetch("/api/trading/predict/execute", {
            method: "POST",
            body: JSON.stringify({
              marketId: position.conditionId || position.marketSlug,
              side: "SELL",
              price: position.currentPrice || 0.5,
              amount: shares * (position.currentPrice || 0.5),
              outcome: position.outcome,
              outcomeIndex: position.outcomeIndex,
            }),
          });
        } else {
          // Polymarket sell — pass tokenId to avoid Gamma slug lookup.
          //
          // 2026-05-21: was passing `Math.floor(shares)` which truncated
          // every position to its integer share count. For 17.79 shares
          // that meant we sold 17 and left 0.79 stranded as dust in DB
          // + on chain — every "Sell All" produced ~0.1-0.9 share dust
          // per market that the user couldn't trade out of. The backend
          // /api/trade/sell already quantizes to the correct
          // minShareIncrement for the market price tier (1.0 for high-
          // priced binary markets, 0.01 for penny markets) and the
          // worker uses the exact value when sellPercent === 1, so we
          // just pass the real share count and let the trading service
          // decide the rounding.
          response = await authFetch("/api/trade/sell", {
            method: "POST",
            body: JSON.stringify({
              marketSlug: position.marketSlug,
              outcome: position.outcome?.toUpperCase(),
              shares,
              price: position.currentPrice,
              orderType: "market",
              ...(position.tokenId ? { tokenId: position.tokenId } : {}),
            }),
          });
        }

        const result = await response.json();
        if (response.ok && result.success) successCount++;
        else failedCount++;
      } catch (err) {
        failedCount++;
        logger.error("Sell-all: failed to sell position", {
          marketSlug: position.marketSlug,
          error: err instanceof Error ? err.message : String(err),
        });
      }

      if (abortController.signal.aborted) break;
      setSellAllProgress(prev => ({ ...prev, success: successCount, failed: failedCount }));
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    if (!abortController.signal.aborted) {
      setSellAllLoading(false);
      setSellAllComplete(true);
      // Tell the rest of the app that positions + balance just changed.
      // The single-sell flow goes through TradingPanel, which dispatches
      // these on success — this loop didn't, so the header balance and
      // other position surfaces stayed stale until the next passive poll.
      // Listeners already retry at 0/1.5s/4s with `fresh=1`, so one fire
      // is enough.
      notifyPortfolioRefresh();
    }
    sellAllAbortRef.current = null;
  };

  // Abort sell-all on unmount
  useEffect(() => {
    return () => {
      sellAllAbortRef.current?.abort();
    };
  }, []);

  // Get market image - backend now fetches from Gamma API
  const getMarketImage = (position: Position) => {
    return position.marketImage || null;
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {/* Loading shimmer header */}
        <div className="px-1">
          <div className="h-3 w-24 skeleton-shimmer rounded" />
        </div>
        {/* Skeleton cards */}
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white dark:bg-[#18181D] rounded-[5px] p-3 border border-gray-200 dark:border-[#1A1A1A]" style={{ animationDelay: `${i * 100}ms` }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 skeleton-shimmer rounded-[5px]" />
              <div className="flex-1">
                <div className="h-3.5 w-2/3 skeleton-shimmer rounded mb-1.5" />
                <div className="h-3 w-1/3 skeleton-shimmer rounded" />
              </div>
              <div className="w-16 h-8 skeleton-shimmer rounded-[5px]" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-[5px]">
        <FiAlertCircle className="w-4 h-4 text-red-500" />
        <p className="text-sm text-red-400">{error}</p>
      </div>
    );
  }

  const isClosed = statusFilter === 'closed';
  // Closed pagina a 15/página; Active muestra todas (suelen ser pocas).
  const CLOSED_PAGE_SIZE = 15;
  const closedPageCount = Math.max(1, Math.ceil(filteredPositions.length / CLOSED_PAGE_SIZE));
  const pagedPositions = isClosed
    ? filteredPositions.slice(closedPage * CLOSED_PAGE_SIZE, closedPage * CLOSED_PAGE_SIZE + CLOSED_PAGE_SIZE)
    : filteredPositions;

  // Empty state component (reused below)
  const renderEmptyState = () => {
    const hasRedeemables = (redeemableData?.count || 0) > 0;
    return (
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] p-6 text-center border border-gray-200 dark:border-[#1A1A1A] transition-colors">
        <div className="w-12 h-12 mx-auto mb-3 rounded-[5px] flex items-center justify-center bg-gray-100 dark:bg-[#0f0f12]">
          <FiPackage className={`w-6 h-6 ${!isClosed && hasRedeemables ? "text-emerald-500" : "text-gray-400"}`} />
        </div>
        <h3 className="font-monument text-base text-gray-900 dark:text-white mb-1">
          {isClosed ? t("portfolio.positions.empty.closedTitle") : t("portfolio.positions.empty.openTitle")}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          {isClosed
            ? t("portfolio.positions.empty.closedSubtitle")
            : hasRedeemables
              ? ((redeemableData?.count || 0) > 1
                  ? t("portfolio.positions.empty.claimableMany", { amount: formatCurrency(redeemableData?.totalRedeemable || 0), n: redeemableData?.count || 0 })
                  : t("portfolio.positions.empty.claimableOne", { amount: formatCurrency(redeemableData?.totalRedeemable || 0), n: redeemableData?.count || 0 }))
              : t("portfolio.positions.empty.openSubtitle")
          }
        </p>
        {!isClosed && (
          <Link
            href="/"
            className="px-4 py-2 rounded-[5px] bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-satoshi font-medium text-sm hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors active:scale-[0.98]"
          >
            {t("portfolio.positions.exploreMarkets")}
          </Link>
        )}
      </div>
    );
  };

  const totalPnLPercent = summary && summary.totalInvested > 0
    ? (summary.totalPnL / summary.totalInvested) * 100
    : 0;

  // Labels for every SortField — all localized via the
  // portfolio.positions.sort.* namespace (EN + ES).
  const sortLabels: Record<SortField, string> = {
    value: t("portfolio.positions.sort.value"),
    pnl: t("portfolio.positions.sort.pnl"),
    percent: t("portfolio.positions.sort.percent"),
    avgPrice: t("portfolio.positions.sort.avgPrice"),
    currentPrice: t("portfolio.positions.sort.currentPrice"),
    date: statusFilter === 'closed' ? t("portfolio.positions.sort.dateClosed") : t("portfolio.positions.sort.dateOpened"),
    alphabetical: t("portfolio.positions.sort.alphabetical"),
    traded: t("portfolio.positions.sort.traded"),
  };
  const sortOptions: SortField[] = SORT_OPTIONS[statusFilter];

  return (
    <>
      {/* Tabs + Search + Sort controls — 2026-05-26 (Pika mobile v2):
          Force the two toggle groups onto a single horizontal line on
          mobile (with horizontal scroll if they overflow a 360px
          viewport). Search and Sort drop to a second row. On sm+ the
          inner toggle wrapper switches to `contents` so the layout
          flattens back to the original wrap-everything row. */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 mb-3">
        {/* Mobile-only wrapper for the two toggle groups. `sm:contents`
            removes this div from layout on desktop, so the toggles
            become direct flex children of the outer row (preserves
            the desktop behaviour exactly). On mobile the wrapper
            stays as a horizontal scroll container so both toggles
            sit on one line even when their combined width exceeds
            the viewport. */}
        {/* sm:contents = on desktop these pills flow into the parent row, so
            HScroll's relative wrapper can't be used here. The edge-fade comes
            from a mobile-only mask instead (removed at sm via sm:[mask-image:none]).
            2026-06-10. */}
        <div className="flex items-center gap-2 -mx-4 px-4 overflow-x-auto scrollbar-hide sm:contents [mask-image:linear-gradient(to_right,transparent,black_16px,black_calc(100%-16px),transparent)] sm:[mask-image:none]">
        {/* Active / Closed tabs */}
        <div className="flex p-0.5 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] flex-shrink-0">
          <button
            onClick={() => handleTabChange('open')}
            className={`px-3 py-1.5 rounded-[5px] text-xs font-medium transition-colors ${
              statusFilter === 'open'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {t("portfolio.positions.tab.active")}
          </button>
          <button
            onClick={() => handleTabChange('closed')}
            className={`px-3 py-1.5 rounded-[5px] text-xs font-medium transition-colors ${
              statusFilter === 'closed'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {t("portfolio.positions.tab.closed")}
          </button>
        </div>

        {/* Source filter */}
        {unifiedPositions && (
          <div className="flex p-0.5 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] flex-shrink-0">
            {/* Hyperliquid replaces Predict in the source row — Predict
                is hidden until we wire the position fan-out for it.
                The filter still accepts 'predict' as a value (kept on
                PositionSource), it's just not shown as a tab. */}
            {(['all', 'polymarket', 'kalshi', 'hyperliquid'] as const).map((src) => (
              <button
                key={src}
                onClick={() => setSourceFilter(src)}
                className={`px-2.5 py-1.5 rounded-[5px] text-xs font-medium transition-colors ${
                  sourceFilter === src
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {t(`portfolio.positions.source.${src}`)}
              </button>
            ))}
          </div>
        )}
        </div>{/* close mobile-only sm:contents toggle wrapper */}

        {/* Mobile second row: Search + Sort. On mobile the parent is
            `flex-col` so this row naturally lands below the toggles.
            On sm+ the parent flips to `flex-row flex-wrap` and the
            search regains flex-1 inline behaviour. */}
        <div className="flex items-center gap-2 sm:contents">

        {/* Search */}
        <div className="relative flex-1 min-w-[140px] sm:basis-auto sm:flex-1 sm:min-w-[140px]">
          <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("portfolio.positions.searchPlaceholder")}
            className="w-full pl-8 pr-3 py-1.5 rounded-[5px] text-xs bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-[#2A2A2A]"
          />
        </div>

        {/* Sort — Polymarket-style dropdown. Trigger shows the active
           label + direction chevron; menu lists the options applicable to
           the current tab (Active vs Closed sets differ — see
           SORT_OPTIONS). Click on the active option toggles asc/desc
           without closing the menu so the user can flip direction in
           place; click on a different option selects it. The menu closes
           on outside-click or Escape. */}
        <div className="relative" ref={sortMenuRef}>
          <button
            onClick={() => setSortMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={sortMenuOpen}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-[5px] text-xs font-medium bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[#1f1f25] transition-colors"
          >
            {sortDir === 'desc'
              ? <FiChevronDown className="w-3 h-3 opacity-60" />
              : <FiChevronUp className="w-3 h-3 opacity-60" />}
            <span>{sortLabels[sortBy]}</span>
          </button>

          {sortMenuOpen ? (
            <div
              role="menu"
              className="absolute right-0 top-full mt-1 w-[200px] rounded-[5px] bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] shadow-lg shadow-black/5 dark:shadow-black/30 z-30 overflow-hidden"
            >
              {sortOptions.map((opt) => {
                const isActive = sortBy === opt;
                return (
                  <button
                    key={opt}
                    role="menuitemradio"
                    aria-checked={isActive}
                    onClick={() => handleSortClick(opt)}
                    className={`flex items-center justify-between w-full px-3 py-2 text-xs text-left transition-colors ${
                      isActive
                        ? 'bg-gray-50 dark:bg-white/[0.04] text-gray-900 dark:text-white font-medium'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.02]'
                    }`}
                  >
                    <span>{sortLabels[opt]}</span>
                    {isActive ? (
                      <span className="flex items-center gap-1 text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">
                        {sortDir === 'desc' ? 'desc' : 'asc'}
                        {sortDir === 'desc'
                          ? <FiChevronDown className="w-3 h-3" />
                          : <FiChevronUp className="w-3 h-3" />}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
        </div>{/* close mobile second-row sm:contents wrapper */}
      </div>

      {/* Header with Value/P&L + Sell All (only for Active tab) */}
      {!isClosed && positions.length > 0 && (
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">{t("portfolio.positions.value")}</span>
              <span className="font-monument text-sm text-gray-900 dark:text-white">{formatCurrency(summary?.totalCurrentValue || 0)}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">{t("portfolio.positions.pnl")}</span>
              <span className={`font-monument text-sm ${(summary?.totalPnL || 0) >= 0 ? "text-emerald-500" : "text-red-400"}`}>
                {(summary?.totalPnL || 0) >= 0 ? "+" : ""}{formatCurrency(summary?.totalPnL || 0)}
                <span className="text-xs opacity-70 ml-1">({formatPercent(totalPnLPercent)})</span>
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              title={t("portfolio.positions.refresh")}
              aria-label={t("portfolio.positions.refresh")}
              className="p-1.5 rounded-[5px] text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-[#1A1A1A] hover:bg-gray-50 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50"
            >
              <FiRefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
            </button>
            <button
              onClick={handleSellAllClick}
              className="px-3 py-1.5 rounded-[5px] text-xs font-medium transition-colors flex items-center gap-1.5 bg-[#FF1744]/20 text-red-400 border border-[#FF1744]/30 hover:bg-[#FF1744]/30 active:scale-[0.97]"
            >
              <FiTrendingDown className="w-3 h-3" />
              {t("portfolio.positions.sellAll")}
            </button>
          </div>
        </div>
      )}

      {/* Closed-tab hint — orients newcomers on what lands here. */}
      {isClosed && positions.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
          {t("portfolio.positions.closedHint")}
        </p>
      )}

      {/* Empty state */}
      {positions.length === 0 ? (
        renderEmptyState()
      ) : filteredPositions.length === 0 ? (
        <div className="bg-white dark:bg-[#18181D] rounded-[5px] p-6 text-center border border-gray-200 dark:border-[#1A1A1A]">
          <FiSearch className="w-6 h-6 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">{t("portfolio.positions.noMatch", { q: searchQuery })}</p>
        </div>
      ) : (
        /* Positions List - Column Grid (2026-05-26).
           @container: the ACTIVE grid needs ~680px of fixed tracks/gaps
           PLUS a 150px floor for the market column = ~830px before it can
           render without squeezing the title down to "Is…". The `@[860px]`
           gate leaves headroom over that; narrower containers (laptop +
           expanded sidebar lands ~720-820px) swap to the stacked card
           layout, which wraps the copytrading chips cleanly. */
        <div className="@container space-y-2">
          {/* Desktop column header — same grid template as each card
              below so the labels sit exactly above their values. */}
          <div
            className="hidden @[860px]:grid px-3 py-2 gap-3 items-center text-[10px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400"
            style={{ gridTemplateColumns: isClosed ? CLOSED_GRID_COLS : ACTIVE_GRID_COLS }}
          >
            <span />
            <span>{t("portfolio.positions.col.market")}</span>
            <span className="text-center">{t("portfolio.positions.col.outcome")}</span>
            <span className="text-center">{t("portfolio.positions.col.bought")}</span>
            {!isClosed && <span className="text-center">{t("portfolio.positions.col.shares")}</span>}
            {!isClosed && <span className="text-center">{t("portfolio.positions.col.avg")}</span>}
            {!isClosed && <span className="text-center">{t("portfolio.positions.col.now")}</span>}
            {!isClosed && <span className="text-center">{t("portfolio.positions.col.value")}</span>}
            <span className="text-center">{t("portfolio.positions.col.pnl")}</span>
            <span />
          </div>
          {pagedPositions.map((position) => {
            const positionKey = `${position.conditionId}-${position.tokenId}`;
            const source = getPositionSource(position);
            const unified = getUnifiedPosition(position);
            // Prefer event page with preselected market + outcome for multi-outcome events
            let href: string;
            if (unified?.href) {
              href = unified.href;
            } else {
              href = marketPageUrl({
                marketSlug: position.marketSlug,
                eventSlug: position.eventSlug,
                outcome: position.outcome,
                source: source || 'polymarket',
                isMultiOutcome: position.isMultiOutcome,
              });
            }

            return (
              <PositionCard
                key={positionKey}
                position={position}
                isMenuOpen={expandedMenu === positionKey}
                hasMergePair={source === "polymarket" && !!mergeablePairs[position.conditionId]}
                isClosed={isClosed}
                marketImage={getMarketImage(position)}
                source={source}
                leaderName={position.leaderName}
                leaderImage={(position as any).leaderImage}
                onSell={() => handleSell(position)}
                onShare={() => handleShare(position)}
                onMerge={() => handleMerge(position)}
                onNavigate={() => { router.push(href); setExpandedMenu(null); }}
                onToggleMenu={() => setExpandedMenu(expandedMenu === positionKey ? null : positionKey)}
                t={t}
              />
            );
          })}
          {/* Paginación — solo en Closed cuando hay más de una página. */}
          {isClosed && filteredPositions.length > CLOSED_PAGE_SIZE && (
            <div className="flex items-center justify-between px-1 pt-2">
              <button
                type="button"
                onClick={() => setClosedPage((p) => Math.max(0, p - 1))}
                disabled={closedPage === 0}
                className="px-3 py-1.5 rounded-[5px] text-[12px] font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#0f0f12] hover:bg-gray-200 dark:hover:bg-[#151518] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ← {t("ct.pagination.prev")}
              </button>
              <span className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                {t("ct.pagination.pageOf", { page: closedPage + 1, total: String(closedPageCount) })}
              </span>
              <button
                type="button"
                onClick={() => setClosedPage((p) => Math.min(closedPageCount - 1, p + 1))}
                disabled={closedPage >= closedPageCount - 1}
                className="px-3 py-1.5 rounded-[5px] text-[12px] font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#0f0f12] hover:bg-gray-200 dark:hover:bg-[#151518] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {t("ct.pagination.next")} →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Share Modal */}
      {selectedPosition && (
        <SharePositionModal
          isOpen={shareModalOpen}
          onClose={() => {
            setShareModalOpen(false);
            setSelectedPosition(null);
          }}
          position={{
            outcome: selectedPosition.outcome,
            outcomeIndex: selectedPosition.outcomeIndex,
            shares: selectedPosition.shares,
            avgPrice: selectedPosition.avgPrice,
            currentPrice: selectedPosition.currentPrice,
            invested: selectedPosition.invested,
            currentValue: selectedPosition.currentValue,
            totalPnL: selectedPosition.totalPnL,
          }}
          marketQuestion={selectedPosition.marketQuestion}
          marketSlug={selectedPosition.marketSlug}
          username={userProfile?.username || userProfile?.name || t("portfolio.trader")}
          userAvatar={userProfile?.image}
          marketImage={getMarketImage(selectedPosition) || undefined}
          source={getPositionSource(selectedPosition)}
          closedResult={getClosedResult(selectedPosition)}
        />
      )}

      {/* Merge Modal */}
      {selectedPosition && mergeablePairs[selectedPosition.conditionId] && (
        <MergePositionsModal
          isOpen={mergeModalOpen}
          onClose={() => {
            setMergeModalOpen(false);
            setSelectedPosition(null);
          }}
          yesPosition={mergeablePairs[selectedPosition.conditionId].yes}
          noPosition={mergeablePairs[selectedPosition.conditionId].no}
          maxMerge={mergeablePairs[selectedPosition.conditionId].maxMerge}
          onSuccess={() => fetchPositions()}
        />
      )}

      {/* Sell All Confirmation Modal */}
      {sellAllModalOpen && createPortal(
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
            onClick={() => !sellAllLoading && setSellAllModalOpen(false)}
          />

          {/* Modal */}
          <div className="relative bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#25252B] rounded-[5px] w-full max-w-sm p-5 shadow-xl shadow-black/10 dark:shadow-black/40">
            {!sellAllLoading && !sellAllComplete && (
              <button
                onClick={() => setSellAllModalOpen(false)}
                className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                <FiX className="w-4 h-4" />
              </button>
            )}

            {!sellAllLoading && !sellAllComplete ? (
              <>
                <div className="mb-5">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t("portfolio.positions.sellAllTitle")}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {t("portfolio.positions.sellAllSubtitle", { n: positions.length })}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] p-3.5 mb-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">{t("portfolio.positions.totalValue")}</span>
                    <span className="text-gray-900 dark:text-white font-medium">{formatCurrency(summary?.totalCurrentValue || 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400">{t("portfolio.positions.unrealizedPnl")}</span>
                    <span className={`font-medium ${(summary?.totalPnL || 0) >= 0 ? "text-emerald-500" : "text-red-400"}`}>
                      {(summary?.totalPnL || 0) >= 0 ? "+" : ""}{formatCurrency(summary?.totalPnL || 0)}
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-400 mb-4">
                  {t("portfolio.positions.sellAllWarning")}
                </p>

                <div className="flex gap-2.5">
                  <button
                    onClick={() => setSellAllModalOpen(false)}
                    className="flex-1 py-2.5 rounded-[5px] text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                  >
                    {t("portfolio.positions.cancel")}
                  </button>
                  <button
                    onClick={executeSellAll}
                    className="flex-1 py-2.5 rounded-[5px] bg-red-500 text-white font-semibold text-sm hover:bg-red-600 transition-colors"
                  >
                    {t("portfolio.positions.sellAll")}
                  </button>
                </div>
              </>
            ) : sellAllLoading ? (
              <>
                <div className="text-center mb-5">
                  <div className="w-10 h-10 mx-auto mb-3 border-2 border-gray-200 dark:border-gray-700 border-t-gray-900 dark:border-t-white rounded-full animate-spin" />
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t("portfolio.positions.selling")}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t("portfolio.positions.progressOf", { cur: sellAllProgress.current, total: sellAllProgress.total })}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] p-3.5 mb-3">
                  <div className="h-1.5 bg-gray-200 dark:bg-[#25252B] rounded-[5px] overflow-hidden mb-3">
                    <div
                      className="h-full bg-gray-900 dark:bg-white rounded-[5px] transition-all duration-300"
                      style={{ width: `${(sellAllProgress.current / sellAllProgress.total) * 100}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-emerald-500">{t("portfolio.positions.soldCount", { n: sellAllProgress.success })}</span>
                    {sellAllProgress.failed > 0 && (
                      <span className="text-red-400">{t("portfolio.positions.failedCount", { n: sellAllProgress.failed })}</span>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="text-center mb-5">
                  <div className="w-10 h-10 mx-auto mb-3 rounded-[5px] bg-emerald-500/10 flex items-center justify-center">
                    <FiCheck className="w-5 h-5 text-emerald-500" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">{t("portfolio.positions.done")}</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {sellAllProgress.failed > 0
                      ? t("portfolio.positions.summarySoldFailed", { success: sellAllProgress.success, failed: sellAllProgress.failed })
                      : t("portfolio.positions.summarySold", { success: sellAllProgress.success })}
                  </p>
                </div>

                <div className="bg-gray-50 dark:bg-[#0f0f12] rounded-[5px] p-3.5 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-emerald-500">{t("portfolio.positions.successful")}</span>
                    <span className="text-gray-900 dark:text-white font-medium">{sellAllProgress.success}</span>
                  </div>
                  {sellAllProgress.failed > 0 && (
                    <div className="flex justify-between text-sm mt-2">
                      <span className="text-red-400">{t("portfolio.positions.failed")}</span>
                      <span className="text-gray-900 dark:text-white font-medium">{sellAllProgress.failed}</span>
                    </div>
                  )}
                </div>

                <button
                  onClick={() => {
                    setSellAllModalOpen(false);
                    setSellAllComplete(false);
                  }}
                  className="w-full py-2.5 rounded-[5px] bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-semibold text-sm hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
                >
                  {t("portfolio.positions.done")}
                </button>
              </>
            )}
          </div>
        </div>,
        document.body
      )}

    </>
  );
}
